import React, { useState } from 'react';
import { Grid, Typography, Button, CardContent, Box, Tooltip, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, useTheme, TextField, InputAdornment } from '@mui/material';
import CustomCard from '../../components/@extended/CustomCard';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  useGetFianceAdminByIdQuery,
  useGetSponsorAdminByIdQuery,
  useGetSponsorByIdQuery,
  useUpdateSponsorDetailsMutation,
  useGetSponsorLevelUsersQuery,
  useAddSponsorFinanceDetailMutation,
} from '../../store/slices/apiSlice';
import { openMessage } from '../../store/slices/showMessageSlice';
import BankDetailsForm from '../../components/common/BankDetailsForm';
import RightArrow from '../../components/icons/RightArrow';
import OrganizationLogo from './OrganizationLogo';
import SponsorAdmin from './SponsorAdminTable';
import DataManagerTable from './DataManagerTable';
import TrailDetailsTable from '../createTrialSystemAdmin/TrailDetailsTable';
import SitesTable from './SitesTable';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignConfiguration from '../CreateSponsor/AssignConfiguration';

const LabelContainer = ({ 
  label, 
  value, 
  valueFontWeight, 
  valueColor, 
  fieldName, 
  onEdit, 
  isEditing, 
  onSave, 
  onCancel, 
  onValueChange,
  editable = true 
}) => {
  const [hovered, setHovered] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onSave(fieldName, editValue);
  };

  const handleCancel = () => {
    setEditValue(value);
    onCancel(fieldName);
  };

  const handleValueChange = (event) => {
    setEditValue(event.target.value);
    if (onValueChange) {
      onValueChange(fieldName, event.target.value);
    }
  };

  return (
    <Grid container py={1} ml={8}>
      <Grid item xs={4} md={5}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8} md={7}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            '&:hover .edit-icon': {
              opacity: editable ? 1 : 0,
            },
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {isEditing ? (
            <TextField
              value={editValue}
              onChange={handleValueChange}
              variant="outlined"
              size="small"
              // fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSave} color="primary">
                      <SaveIcon />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancel} color="error">
                      <CancelIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <>
              <Typography variant="body1" fontWeight={valueFontWeight} color={valueColor}>
                {value}
              </Typography>
              {editable && (
                <IconButton
                  size="small"
                  className="edit-icon"
                  onClick={() => onEdit(fieldName)}
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    ml: 1,
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

const SponsorShow = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sponsorId } = useParams();
  const theme = useTheme();
  const { data: organizationDetails, isLoading, refetch: refetchSponsorDetails } = useGetSponsorByIdQuery(sponsorId);
  const { data: data1, isLoading: isLoading1 } = useGetSponsorAdminByIdQuery(sponsorId);
  const { data: data2, isLoading: isLoading2 } = useGetFianceAdminByIdQuery(sponsorId);
  // Fetch all sponsor-level users (Data Managers and Site Monitors)
  const { data: data3, isLoading: isLoading3 } = useGetSponsorLevelUsersQuery({ sponsorId });
  const [updateSponsorDetails] = useUpdateSponsorDetailsMutation();
  const [addSponsorFinanceDetail, { isLoading: isSavingBankDetails }] = useAddSponsorFinanceDetailMutation();
  const [openAddAdmin, setOpenAddAdmin] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [openBankDetails, setOpenBankDetails] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({});

  const financeStatus = organizationDetails?.financeDetail?.status?.toLowerCase();
  const isFinanceActive = financeStatus === 'active';
  const canManageBankDetails = !isFinanceActive;
  const hasExistingFinanceDetail = Boolean(organizationDetails?.financeDetail);
  const bankDetailsMenuLabel = hasExistingFinanceDetail
    ? 'Complete Bank Details'
    : 'Add Bank Details';

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenBankDetails = () => {
    setOpenBankDetails(true);
  };

  const handleCloseBankDetails = () => {
    setOpenBankDetails(false);
  };

  const saveSponsorBankDetails = async (data) => {
    try {
      const payload = { ...data, businessType: 'company' };
      await addSponsorFinanceDetail({
        payload,
        sponsorId,
      }).unwrap();
      dispatch(
        openMessage({
          message: 'Sponsor bank details added successfully.',
          messageSeverity: 'success',
        })
      );
      handleCloseBankDetails();
      refetchSponsorDetails();
    } catch (error) {
      console.error('Error saving sponsor bank details:', error);
      throw error;
    }
  };

  const handleEdit = (fieldName) => {
    setEditingField(fieldName);
    setFormData(prev => ({
      ...prev,
      [fieldName]: organizationDetails?.[fieldName] || ''
    }));
  };

  const handleSave = async (fieldName, value) => {
    try {
      const updateData = {
        [fieldName]: value
      };
      
      await updateSponsorDetails({
        sponsorId,
        body: updateData
      }).unwrap();
      
      setEditingField(null);
      setFormData(prev => ({
        ...prev,
        [fieldName]: value
      }));
    } catch (error) {
      console.error('Error updating sponsor details:', error);
    }
  };

  const handleCancel = (fieldName) => {
    setEditingField(null);
    setFormData(prev => {
      const newData = { ...prev };
      delete newData[fieldName];
      return newData;
    });
  };

  const handleValueChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  if (isLoading) {
    return <></>;
  }
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Button type="text" onClick={() => navigate('/')} startIcon={<RightArrow leftArrow />}>
          <Typography variant="subtitle1" sx={{ textTransform: 'none' }}>
            Back
          </Typography>
        </Button>
      </Grid>
      <Grid item xs={12}>
        <CustomCard
          title={
            <Typography variant="h6" fontWeight={600} color={theme.palette.grey[800]}>
              Sponsor Organization Details
            </Typography>
          }
          action={
            <Box>
              <Tooltip title={'More'}>
                <IconButton variant="outlined" fontSize="small" onClick={handleClick}>
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                onClick={handleCloseMenu}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    '&::before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem
                  onClick={() => {
                    handleCloseMenu();
                    handleOpen();
                  }}
                >
                  Manage Configuration
                </MenuItem>
                {canManageBankDetails && (
                  <MenuItem
                    onClick={() => {
                      handleCloseMenu();
                      handleOpenBankDetails();
                    }}
                  >
                    {bankDetailsMenuLabel}
                  </MenuItem>
                )}
              </Menu>
            </Box>
          }
        >
          <Dialog
            open={open}
            onClose={() => {
              handleClose();
            }}
          >
            <DialogTitle>Manage Configuration</DialogTitle>
            <IconButton
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
              onClick={() => {
                handleClose();
              }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent sx={{ pt: 0 }}>
              <AssignConfiguration handleClose={handleClose} sponsorId={sponsorId} />
            </DialogContent>
          </Dialog>
          <Dialog
            open={openBankDetails}
            onClose={handleCloseBankDetails}
            PaperProps={{
              sx: {
                maxWidth: '800px',
                width: { xs: '100%', md: 800 },
              },
            }}
          >
            <DialogTitle>{bankDetailsMenuLabel}</DialogTitle>
            <IconButton
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
              onClick={handleCloseBankDetails}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent sx={{ pt: 0 }}>
              <BankDetailsForm
                handleClose={handleCloseBankDetails}
                handleSave={saveSponsorBankDetails}
                isLoading={isSavingBankDetails}
              />
            </DialogContent>
          </Dialog>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={160}>
                  <OrganizationLogo organizationDetails={organizationDetails} />
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <LabelContainer 
                  label={'Organization Name'} 
                  value={organizationDetails?.orgnizationName || 'N/A'} 
                  fieldName="orgnizationName"
                  isEditing={editingField === 'orgnizationName'}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onValueChange={handleValueChange}
                />
                <LabelContainer
                  label={'Organization Category'}
                  value={organizationDetails?.orgnizationCategory || 'N/A'}
                  fieldName="orgnizationCategory"
                  isEditing={editingField === 'orgnizationCategory'}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onValueChange={handleValueChange}
                />
                <LabelContainer 
                  label={'Address'} 
                  value={organizationDetails?.address || 'N/A'} 
                  fieldName="address"
                  isEditing={editingField === 'address'}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onValueChange={handleValueChange}
                />
                <LabelContainer 
                  label={'Contact Number'} 
                  value={organizationDetails?.contactNumber || 'N/A'} 
                  fieldName="contactNumber"
                  isEditing={editingField === 'contactNumber'}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onValueChange={handleValueChange}
                />
                <LabelContainer 
                  label={'Fax'} 
                  value={organizationDetails?.fax || 'N/A'} 
                  fieldName="fax"
                  isEditing={editingField === 'fax'}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onValueChange={handleValueChange}
                />
                <LabelContainer 
                  label={'Contact Person'} 
                  value={organizationDetails?.contactPerson || 'N/A'} 
                  fieldName="contactPerson"
                  isEditing={editingField === 'contactPerson'}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onValueChange={handleValueChange}
                />
                <LabelContainer 
                  label={'Contact Email'} 
                  value={organizationDetails?.contactEmail || 'N/A'} 
                  fieldName="contactEmail"
                  isEditing={editingField === 'contactEmail'}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onValueChange={handleValueChange}
                />
                <LabelContainer
                  label={'Bank Verification Status'}
                  value={isFinanceActive ? 'Active' : 'Pending'}
                  valueFontWeight={600}
                  valueColor={
                    isFinanceActive
                      ? theme.palette.success.main
                      : theme.palette.warning.main
                  }
                  editable={false}
                />
              </Grid>
            </Grid>
          </CardContent>
        </CustomCard>
      </Grid>
      <Grid item sm={12}>
        <SponsorAdmin openAddAdmin={openAddAdmin} data={data1} isLoading={isLoading1} />
      </Grid>
      {organizationDetails?.financeDetail?.entityId && (
        <Grid item sm={12}>
          <SponsorAdmin openAddAdmin={openAddAdmin} type="fi_admin" data={data2} isLoading={isLoading2} />
        </Grid>
      )}
      <Grid item sm={12}>
        <DataManagerTable 
          openAddAdmin={openAddAdmin} 
          data={data3} 
          isLoading={isLoading3} 
          showUserTypeDropdown={true}
        />
      </Grid>
      <Grid item sm={12}>
        <SitesTable />
      </Grid>
      <Grid item sm={12}>
        <TrailDetailsTable />
      </Grid>
    </Grid>
  );
};

export default SponsorShow;
